// "Recent runs" section of the NodeDrawer — the per-run transactions table for
// jobSource nodes (spec §5 / §3.3, D7).
//
// Fetches lightweight recent rows via `admin_pipeline_jobs` { mode: 'recent' }
// and renders them as a table; clicking a row swaps in <JobDetail/> for the full
// (heavy-JSON) view of that single run.
//
// SECURITY: status messages etc. render as React children → auto-escaped.
import { useEffect, useState } from "react";
import { invokeAdmin, errMsg } from "../../lib/adminApi";
import type { JobRecentRow } from "../../lib/types";
import { Badge, Banner } from "../ui";
import JobDetail from "./JobDetail";

const fmt = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

export default function JobRunsPanel({
  table,
  type,
  limit = 25,
}: {
  table: "event" | "place";
  type?: "website" | "instagram";
  limit?: number;
}) {
  const [rows, setRows] = useState<JobRecentRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<JobRecentRow | null>(null);

  useEffect(() => {
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-request loading/error reset when the request key changes; results are only written after the await, guarded by the `cancelled` flag
    setLoading(true);
    setError(null);
    setSelected(null);
    invokeAdmin<{ rows: JobRecentRow[] }>("admin_pipeline_jobs", {
      mode: "recent",
      table,
      type,
      limit,
      offset: 0,
    })
      .then((r) => {
        if (!cancelled) setRows(r.rows);
      })
      .catch((e) => {
        if (!cancelled) setError(errMsg(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [table, type, limit]);

  if (selected) {
    return (
      <JobDetail table={selected.table} id={selected.id} onBack={() => setSelected(null)} />
    );
  }

  return (
    <div className="mt-1">
      {loading && <p className="text-xs text-zinc-500">Loading recent runs…</p>}
      {error && <Banner className="mt-1">{error}</Banner>}
      {rows && rows.length === 0 && !loading && (
        <p className="text-xs text-zinc-500">No runs recorded yet.</p>
      )}
      {rows && rows.length > 0 && (
        <div className="overflow-hidden rounded border border-zinc-800">
          <table className="w-full text-xs">
            <thead className="bg-zinc-950 text-left text-[10px] uppercase tracking-wide text-zinc-500">
              <tr>
                <th className="px-2 py-1 font-medium">Status</th>
                <th className="px-2 py-1 font-medium">Type</th>
                <th className="px-2 py-1 font-medium">Message</th>
                <th className="px-2 py-1 font-medium">When</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`View run ${r.id}`}
                  onClick={() => setSelected(r)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setSelected(r);
                    }
                  }}
                  className="cursor-pointer border-t border-zinc-800 hover:bg-zinc-800/50 focus:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-zinc-400"
                >
                  <td className="px-2 py-1">
                    <Badge ok={r.statusSuccess !== false}>
                      {r.statusSuccess === false ? "fail" : "ok"}
                    </Badge>
                  </td>
                  <td className="px-2 py-1 text-zinc-400">
                    {r.type ??
                      (table === "place"
                        ? r.placeCreated
                          ? "created"
                          : "place"
                        : r.statusType ?? "—")}
                  </td>
                  <td className="max-w-[12rem] truncate px-2 py-1 text-zinc-400">
                    {r.statusMessage ?? "—"}
                  </td>
                  <td className="whitespace-nowrap px-2 py-1 font-mono text-[10px] text-zinc-500">
                    {fmt(r.at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
